import { PDFDocument, degrees } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import { createToolItemId } from '../../toolPageUtils';
import { getPdfPageCount } from '../split/splitPdfEngine';
import {
    flattenPerspective,
    getOutputDimensions,
} from '../../image/flatten/flattenDocumentEngine';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

/** PDFs larger than this skip the per-page editor (too many thumbnails to render). */
export const MAX_EDIT_PAGES = 60;
/** Higher cap for editing-centric tools (rotate / remove / crop / resize). */
export const MAX_EDIT_PAGES_LARGE = 300;

export const DEFAULT_COLOR_ADJUST = {
    colorStyle: 'none',
    brightness: 100,
    contrast: 100,
    saturation: 100,
    grayscale: 0,
    sepia: 0,
};

/** Named presets applied from the color modal style chips. */
export const COLOR_STYLE_IDS = {
    none: { ...DEFAULT_COLOR_ADJUST },
    enhance: {
        colorStyle: 'enhance',
        brightness: 105,
        contrast: 122,
        saturation: 108,
        grayscale: 0,
        sepia: 0,
    },
    grayscale: {
        colorStyle: 'grayscale',
        brightness: 100,
        contrast: 112,
        saturation: 100,
        grayscale: 100,
        sepia: 0,
    },
    bw: {
        colorStyle: 'bw',
        brightness: 112,
        contrast: 175,
        saturation: 100,
        grayscale: 100,
        sepia: 0,
    },
    sepia: {
        colorStyle: 'sepia',
        brightness: 104,
        contrast: 108,
        saturation: 100,
        grayscale: 0,
        sepia: 75,
    },
    vivid: {
        colorStyle: 'vivid',
        brightness: 105,
        contrast: 118,
        saturation: 165,
        grayscale: 0,
        sepia: 0,
    },
    soft: {
        colorStyle: 'soft',
        brightness: 108,
        contrast: 88,
        saturation: 82,
        grayscale: 0,
        sepia: 0,
    },
};

export function normalizeColorAdjust(pageOrColor = {}) {
    return {
        colorStyle: pageOrColor.colorStyle || 'none',
        brightness: Number.isFinite(pageOrColor.brightness) ? pageOrColor.brightness : 100,
        contrast: Number.isFinite(pageOrColor.contrast) ? pageOrColor.contrast : 100,
        saturation: Number.isFinite(pageOrColor.saturation) ? pageOrColor.saturation : 100,
        grayscale: Number.isFinite(pageOrColor.grayscale) ? pageOrColor.grayscale : 0,
        sepia: Number.isFinite(pageOrColor.sepia) ? pageOrColor.sepia : 0,
    };
}

export function pageHasColorAdjust(page) {
    const c = normalizeColorAdjust(page);
    return (
        c.brightness !== 100
        || c.contrast !== 100
        || c.saturation !== 100
        || c.grayscale !== 0
        || c.sepia !== 0
    );
}

export function buildColorFilterCss(pageOrColor) {
    const c = normalizeColorAdjust(pageOrColor);
    if (!pageHasColorAdjust(c)) return undefined;
    return `brightness(${c.brightness}%) contrast(${c.contrast}%) saturate(${c.saturation}%) grayscale(${c.grayscale}%) sepia(${c.sepia}%)`;
}

export function applyColorAdjustments(srcCanvas, pageOrColor) {
    const css = buildColorFilterCss(pageOrColor);
    if (!css) return srcCanvas;
    const out = document.createElement('canvas');
    out.width = srcCanvas.width;
    out.height = srcCanvas.height;
    const ctx = out.getContext('2d');
    ctx.filter = css;
    ctx.drawImage(srcCanvas, 0, 0);
    ctx.filter = 'none';
    return out;
}

async function loadPdf(file) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        const message = String(err?.message || '').toLowerCase();
        if (message.includes('password') || message.includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

async function loadPdfJs(file) {
    const data = new Uint8Array(await file.arrayBuffer());
    try {
        return await pdfjsLib.getDocument({ data }).promise;
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('password')) {
            throw new Error('password');
        }
        throw err;
    }
}

function applyCropRegion(page, region) {
    const { width: pw, height: ph } = page.getSize();
    const cropW = Math.max(1, region.width * pw);
    const cropH = Math.max(1, region.height * ph);
    const x = region.left * pw;
    const y = ph - region.top * ph - cropH;
    page.setCropBox(x, y, cropW, cropH);
    page.setMediaBox(x, y, cropW, cropH);
}

async function renderPageToBlobUrl(file, pageIndex, scale = 2) {
    const pdfJs = await loadPdfJs(file);
    const page = await pdfJs.getPage(pageIndex + 1);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport, background: '#ffffff' }).promise;

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => {
            if (value) resolve(value);
            else reject(new Error('flattenFailed'));
        }, 'image/jpeg', 0.95);
    });
    return {
        url: URL.createObjectURL(blob),
        width: canvas.width,
        height: canvas.height,
        viewport,
        scale,
        canvas,
    };
}

async function blobToCanvas(blob) {
    const url = URL.createObjectURL(blob);
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        return canvas;
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function canvasToJpegBlob(canvas, quality = 0.92) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((value) => {
            if (value) resolve(value);
            else reject(new Error('flattenFailed'));
        }, 'image/jpeg', quality);
    });
}

async function applyColorToBlob(blob, color) {
    if (!pageHasColorAdjust(color)) return blob;
    const canvas = await blobToCanvas(blob);
    const adjusted = applyColorAdjustments(canvas, color);
    return canvasToJpegBlob(adjusted);
}

async function embedJpegPage(outPdf, blob, pageW, pageH, rotationDegrees) {
    const embedded = await outPdf.embedJpg(await blob.arrayBuffer());
    const newPage = outPdf.addPage([pageW, pageH]);
    newPage.drawImage(embedded, { x: 0, y: 0, width: pageW, height: pageH });
    const rotation = (((rotationDegrees || 0) % 360) + 360) % 360;
    if (rotation) newPage.setRotation(degrees(rotation));
}

/**
 * Rasterize a rectangular crop region into a new page.
 * @param {import('pdf-lib').PDFDocument} outPdf
 */
export async function flattenCropToNewPage(outPdf, file, pageIndex, region, rotationDegrees, pageSize, color) {
    const colorAdjust = normalizeColorAdjust(color);
    const rendered = await renderPageToBlobUrl(file, pageIndex);
    try {
        const corners = [
            { x: region.left * rendered.width, y: region.top * rendered.height },
            { x: (region.left + region.width) * rendered.width, y: region.top * rendered.height },
            { x: (region.left + region.width) * rendered.width, y: (region.top + region.height) * rendered.height },
            { x: region.left * rendered.width, y: (region.top + region.height) * rendered.height },
        ];
        const { width: outW, height: outH } = getOutputDimensions(corners);
        let blob = await flattenPerspective(
            rendered.url,
            { width: rendered.width, height: rendered.height },
            0,
            corners,
            outW,
            outH,
            'image/jpeg',
        );
        if (!blob) throw new Error('flattenFailed');
        blob = await applyColorToBlob(blob, colorAdjust);

        const pageW = Math.max(1, (pageSize?.width || rendered.viewport.width / rendered.scale) * region.width);
        const pageH = Math.max(1, (pageSize?.height || rendered.viewport.height / rendered.scale) * region.height);
        await embedJpegPage(outPdf, blob, pageW, pageH, rotationDegrees);
    } finally {
        URL.revokeObjectURL(rendered.url);
    }
}

/**
 * Perspective-warp a 4-corner selection (TL,TR,BR,BL normalized 0–1) into a flat page.
 * @param {import('pdf-lib').PDFDocument} outPdf
 * @param {{ x: number, y: number }[]} corners
 */
export async function flattenPerspectiveCropToNewPage(outPdf, file, pageIndex, corners, rotationDegrees, pageSize, color) {
    if (!Array.isArray(corners) || corners.length < 4) {
        throw new Error('flattenFailed');
    }

    const colorAdjust = normalizeColorAdjust(color);
    const rendered = await renderPageToBlobUrl(file, pageIndex);
    try {
        const naturalCorners = corners.map((c) => ({
            x: c.x * rendered.width,
            y: c.y * rendered.height,
        }));
        const { width: outW, height: outH } = getOutputDimensions(naturalCorners);
        let blob = await flattenPerspective(
            rendered.url,
            { width: rendered.width, height: rendered.height },
            0,
            naturalCorners,
            outW,
            outH,
            'image/jpeg',
        );
        if (!blob) throw new Error('flattenFailed');
        blob = await applyColorToBlob(blob, colorAdjust);

        const xs = corners.map((c) => c.x);
        const ys = corners.map((c) => c.y);
        const regionW = Math.max(...xs) - Math.min(...xs);
        const regionH = Math.max(...ys) - Math.min(...ys);
        const baseW = pageSize?.width || rendered.viewport.width / rendered.scale;
        const baseH = pageSize?.height || rendered.viewport.height / rendered.scale;
        const aspect = outW / Math.max(1, outH);
        let pageW = Math.max(1, baseW * Math.max(regionW, 0.05));
        let pageH = pageW / aspect;
        if (pageH > baseH * 1.5) {
            pageH = Math.max(1, baseH * Math.max(regionH, 0.05));
            pageW = pageH * aspect;
        }

        await embedJpegPage(outPdf, blob, pageW, pageH, rotationDegrees);
    } finally {
        URL.revokeObjectURL(rendered.url);
    }
}

/**
 * Rasterize a full (or crop-box) page and apply brightness/contrast.
 * Used when color is adjusted without flatten mode.
 */
export async function colorAdjustPageToNewPage(outPdf, file, pageIndex, region, rotationDegrees, pageSize, color) {
    const colorAdjust = normalizeColorAdjust(color);
    const rendered = await renderPageToBlobUrl(file, pageIndex);
    try {
        let canvas = rendered.canvas;
        let pageW = pageSize?.width || rendered.viewport.width / rendered.scale;
        let pageH = pageSize?.height || rendered.viewport.height / rendered.scale;

        if (region?.width > 0 && region?.height > 0) {
            const sx = Math.max(0, Math.floor(region.left * canvas.width));
            const sy = Math.max(0, Math.floor(region.top * canvas.height));
            const sw = Math.max(1, Math.floor(region.width * canvas.width));
            const sh = Math.max(1, Math.floor(region.height * canvas.height));
            const cropped = document.createElement('canvas');
            cropped.width = sw;
            cropped.height = sh;
            cropped.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);
            canvas = cropped;
            pageW = Math.max(1, pageW * region.width);
            pageH = Math.max(1, pageH * region.height);
        }

        const adjusted = applyColorAdjustments(canvas, colorAdjust);
        const blob = await canvasToJpegBlob(adjusted);
        await embedJpegPage(outPdf, blob, pageW, pageH, rotationDegrees);
    } finally {
        URL.revokeObjectURL(rendered.url);
    }
}

/**
 * Build the default editable page list for a PDF with `count` pages.
 * @param {number} count
 */
export function createPageEdits(count) {
    return Array.from({ length: count }, (_, pageIndex) => ({
        id: createToolItemId(),
        pageIndex,
        sourcePageNumber: pageIndex + 1,
        rotation: 0,
        removed: false,
        cropRegion: null,
        cropPct: null,
        cropCorners: null,
        cropMode: 'crop',
        ...DEFAULT_COLOR_ADJUST,
    }));
}

/**
 * Read a file's page count and return its editable page list, or the string
 * `'oversize'` when the PDF has too many pages for the editor.
 * @param {File|Blob} file
 * @returns {Promise<Array|'oversize'>}
 */
export async function buildFilePageEdits(file, maxPages = MAX_EDIT_PAGES) {
    const count = await getPdfPageCount(file);
    if (count > maxPages) return 'oversize';
    return createPageEdits(count);
}

/**
 * Whether a page-edit list changes the document.
 */
export function pagesHaveEdits(pages) {
    if (!Array.isArray(pages) || !pages.length) return false;
    return pages.some((p, i) => (
        p.removed
        || (((p.rotation || 0) % 360) !== 0)
        || !!p.cropRegion
        || !!p.cropCorners
        || pageHasColorAdjust(p)
        || p.pageIndex !== i
    ));
}

/**
 * Rebuild a PDF applying per-page edits (drop removed, order, rotate, crop/flatten, color).
 * @param {File|Blob} file
 * @returns {Promise<Blob>}
 */
export async function applyPdfPageEdits(file, pages) {
    const source = await loadPdf(file);
    const kept = pages.filter((p) => !p.removed);
    if (!kept.length) throw new Error('removeAll');

    const out = await PDFDocument.create();

    for (const edit of kept) {
        const rotation = (((edit.rotation || 0) % 360) + 360) % 360;
        const mode = edit.cropMode === 'flatten' ? 'flatten' : 'crop';
        const color = normalizeColorAdjust(edit);
        const needsColor = pageHasColorAdjust(edit);
        const srcPages = source.getPages();
        const srcPage = srcPages[edit.pageIndex];
        const size = srcPage?.getSize?.() || { width: 595, height: 842 };

        if (mode === 'flatten' && (edit.cropCorners?.length === 4 || edit.cropRegion)) {
            if (edit.cropCorners?.length === 4) {
                await flattenPerspectiveCropToNewPage(
                    out, file, edit.pageIndex, edit.cropCorners, rotation, size, color,
                );
            } else {
                await flattenCropToNewPage(
                    out, file, edit.pageIndex, edit.cropRegion, rotation, size, color,
                );
            }
            continue;
        }

        if (needsColor) {
            await colorAdjustPageToNewPage(
                out, file, edit.pageIndex, edit.cropRegion || null, rotation, size, color,
            );
            continue;
        }

        const [page] = await out.copyPages(source, [edit.pageIndex]);
        if (rotation) {
            const current = page.getRotation().angle;
            page.setRotation(degrees((current + rotation) % 360));
        }
        if (edit.cropRegion) applyCropRegion(page, edit.cropRegion);
        out.addPage(page);
    }

    const bytes = await out.save({ useObjectStreams: true });
    return new Blob([bytes], { type: 'application/pdf' });
}
